import { IconSearch } from '@tabler/icons-react'

import { Kbd } from '@/components/sui/kbd'
import {
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@/components/sui/sidebar'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { trackSearchOpened } from '@/lib/analytics'
import { formatShortcut, isMacPlatform } from '@/lib/platform'
import { cn } from '@/lib/utils'

import { SidebarMenuButtonWithTooltip } from '../sidebar-menu-button-with-tooltip'

import locales from './locales'

interface NavSearchProps {
  onOpen: () => void
  className?: string
}

export function NavSearch({ onOpen, className }: NavSearchProps) {
  const { t } = useComponentTranslation(locales)
  const { state, isMobile, setOpenMobile } = useSidebar()
  const isCollapsed = !isMobile && state === 'collapsed'
  const shortcut = formatShortcut('K')
  const keyShortcuts = isMacPlatform() ? 'Meta+K' : 'Control+K'

  const open = () => {
    setOpenMobile(false)
    trackSearchOpened('sidebar')
    onOpen()
  }

  if (isCollapsed) {
    return (
      <SidebarMenu className={className}>
        <SidebarMenuItem>
          <SidebarMenuButtonWithTooltip
            render={
              <button
                type="button"
                aria-keyshortcuts={keyShortcuts}
                onClick={open}
              />
            }
            tooltip={
              <span className="flex items-center gap-2">
                {t('search')}
                <Kbd>{shortcut}</Kbd>
              </span>
            }
          >
            <IconSearch />
            <span>{t('search')}</span>
          </SidebarMenuButtonWithTooltip>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <button
      type="button"
      aria-keyshortcuts={keyShortcuts}
      onClick={open}
      className={cn(
        'group/search border-input bg-background text-muted-foreground hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 flex h-8 w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-md border px-2 text-sm transition-[color,box-shadow] outline-none focus-visible:ring-[3px]',
        className,
      )}
    >
      <IconSearch className="size-4 shrink-0" />
      <span className="flex-1 truncate text-left">{t('search')}</span>
      {!isMobile && (
        <Kbd
          aria-hidden="true"
          className="group-hover/search:text-foreground transition-colors"
        >
          {shortcut}
        </Kbd>
      )}
    </button>
  )
}
